import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

export type AuditEntry = EndpointOutput<ApiContract["GET /admin/audit"]>[number];

const ROLE_LABEL: Record<string, string> = {
  superadmin: "superadmin",
  admin: "admin",
  manager: "gestor(a)",
};

/** Why an MCP call on a dead connection was refused (`mcp:connections.blocked`). */
const BLOCKED_REASON_LABEL: Record<string, string> = {
  revoked: "conexão revogada",
  user_inactive: "conta inativa",
  user_deleted: "conta excluída",
  grant_outdated: "permissões desatualizadas (reconexão necessária)",
};

type McpConnectionPayload = {
  client_id?: string;
  client_name?: string;
  scopes?: unknown;
  reason?: string;
} | null;

/** The app's display name; rows written before it was recorded fall back to its id. */
function mcpClient(p: McpConnectionPayload): string | null {
  return p?.client_name || p?.client_id || null;
}

function permissionCount(scopes: unknown): string | null {
  if (!Array.isArray(scopes)) return null;
  return `${scopes.length} ${scopes.length === 1 ? "permissão" : "permissões"}`;
}

/** OAuth connection lifecycle rows written by the MCP worker; null for any other action. */
function describeMcpConnection(entry: AuditEntry): string | null {
  const p = entry.payload as McpConnectionPayload;
  const client = mcpClient(p);
  const withClient = (label: string, detail?: string | null) =>
    `· ${label}${client ? `: ${client}` : ""}${detail ? ` (${detail})` : ""}`;

  switch (entry.action) {
    case "mcp:connections.create":
      return withClient("Conexão MCP autorizada", permissionCount(p?.scopes));
    case "mcp:connections.revoke":
      return withClient("Conexão MCP revogada");
    case "mcp:connections.deny":
      return withClient("Autorização MCP recusada");
    case "mcp:connections.blocked":
      return withClient(
        "Tentativa com conexão MCP revogada/bloqueada",
        p?.reason ? BLOCKED_REASON_LABEL[p.reason] ?? p.reason : null
      );
    default:
      return null;
  }
}

/**
 * Turn a row into something a person can read.
 *
 * A role change should say who was affected and how — "promoveu
 * ana@x.com de admin para gestor(a)" — not "POST /admin/admins". Rows
 * written before payload capture existed, and rows from handlers that
 * declare nothing, fall back to the verb and the object.
 */
export function describeEntry(entry: AuditEntry): string {
  const p = entry.payload as {
    targetEmail?: string;
    previousRole?: string | null;
    newRole?: string | null;
    keyId?: string;
    keyName?: string | null;
    notified_email?: string | null;
  } | null;

  // The hourly expiry job switching a coupon off.
  if (entry.action === "system:coupons.expire") {
    const code = entry.entity_id ?? "—";
    return p?.notified_email
      ? `· Cupom expirado automaticamente: ${code} (aviso enviado a ${p.notified_email})`
      : `· Cupom expirado automaticamente: ${code} (sem destinatário para o aviso)`;
  }

  const mcpConnection = describeMcpConnection(entry);
  if (mcpConnection) return mcpConnection;

  if (p?.targetEmail && (p.previousRole !== undefined || p.newRole !== undefined)) {
    const to = p.newRole ? ROLE_LABEL[p.newRole] ?? p.newRole : null;
    const from = p.previousRole ? ROLE_LABEL[p.previousRole] ?? p.previousRole : null;

    if (!to) return `removeu o acesso de ${p.targetEmail}`;
    if (!from) return `deu acesso de ${to} a ${p.targetEmail}`;
    return `mudou ${p.targetEmail} de ${from} para ${to}`;
  }

  // Legacy rows from the retired static MCP keys (/admin/mcp/keys, removed
  // with the move to OAuth) carry the key in the payload; without this branch
  // the path-derived entity renders the useless literal "mcp#keys".
  if (p?.keyId) {
    const label = p.keyName ?? p.keyId;
    return entry.action.startsWith("DELETE")
      ? `removeu a chave "${label}"`
      : `alterou a chave "${label}"`;
  }

  const target = entry.entity
    ? `${entry.entity}${entry.entity_id ? `#${entry.entity_id}` : ""}`
    : null;
  return target ? `${entry.action} · ${target}` : entry.action;
}
